import React, { useState, useEffect, useRef } from 'react';
import '../../../styles/dashboard/OrgUser/CreditCheck.css';
import PdfViewerModal from '../../PdfViewerModal/PdfViewerModal';
import OrgUserAuthService from '../../../services/orgUserAuth.service';
import OrgUserService from '../../../services/orgUser.service';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const planDescriptions = {
  Basic: 'Customer data analysis from LCS records',
  Advanced: 'Credit Bureau verification',
  Combo: 'Basic + Advanced + Optional Court Case verification'
};

const splitFullName = (fullName = '') => {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return {
      customerFirstName: '',
      customerMiddleName: '',
      customerLastName: '',
      hasMinimumParts: false,
    };
  }

  if (parts.length === 1) {
    return {
      customerFirstName: parts[0],
      customerMiddleName: '',
      customerLastName: '',
      hasMinimumParts: false,
    };
  }

  return {
    customerFirstName: parts[0],
    customerMiddleName: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
    customerLastName: parts.at(-1) || '',
    hasMinimumParts: true,
  };
};

export default function CreditCheckModal({ open, onClose, planType, mode = 'default', selectedProvider = null }) {
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Mobile-to-Prefill state
  const [prefillMobile, setPrefillMobile] = useState('');
  const [prefillMobile2, setPrefillMobile2] = useState('');
  const [prefillFirstName, setPrefillFirstName] = useState('');
  const [prefillMiddleName, setPrefillMiddleName] = useState('');
  const [prefillLastName, setPrefillLastName] = useState('');
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState('');
  const [prefillResult, setPrefillResult] = useState(null);
  const [prefillCheckId, setPrefillCheckId] = useState(null);
  const [prefillNotFound, setPrefillNotFound] = useState(false); // 1st attempt returned no data
  const [prefillExhausted, setPrefillExhausted] = useState(false); // 2nd attempt also returned no data

  // CIBIL report type — replaces the old SuperAdmin-level v3/v5 toggle with a
  // mandatory per-request choice: 'with-data' -> v3, 'without-data' -> v5.
  const [cibilDataOption, setCibilDataOption] = useState('');
  const [report, setReport] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfStatusChecking, setPdfStatusChecking] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  
  // Court case checkbox for Combo plan
  const [includeCourtCase, setIncludeCourtCase] = useState(false);
  const [courtCaseCost, setCourtCaseCost] = useState(10); // Default cost
  
  // Equifax fields for Advanced Plan (WITH OTP)
  const [equifaxTransactionId, setEquifaxTransactionId] = useState(null);
  const [equifaxReportData, setEquifaxReportData] = useState(null);
  const [showEquifaxStatus, setShowEquifaxStatus] = useState(false);
  
  // Bureau provider fields for Advanced/Combo Plan (WITHOUT OTP)
  const [bureauReportData, setBureauReportData] = useState(null);
  const [bureauReportNumber, setBureauReportNumber] = useState(null);
  const [showBureauStatus, setShowBureauStatus] = useState(false);
  const [courtCaseLoading, setCourtCaseLoading] = useState(false);
  const [courtCaseStatus, setCourtCaseStatus] = useState('');
  const [courtCasePolling, setCourtCasePolling] = useState(false);
  const [courtCaseCheckId, setCourtCaseCheckId] = useState(null);
  const courtCaseTimerRef = useRef(null);
  const courtCaseAttemptsRef = useRef(0);
  const pdfStatusTimerRef = useRef(null);

  // Helper function to get provider display name
  const getProviderDisplayName = () => {
    if (planType === 'Basic') {
      return 'LCS';
    }
    if (mode === 'with-otp') {
      return 'Credit Score';
    }
    if (mode === 'without-otp' && selectedProvider) {
      const providerMap = {
        'cibil': 'CIBIL',
        'crif': 'CRIF HIGH MARK',
        'experian': 'Experian'
      };
      return providerMap[selectedProvider.toLowerCase()] || selectedProvider;
    }
    // Fallback: check report's apiProvider
    if (report?.details?.apiProvider) {
      const providerMap = {
        'equifax': 'Credit Score',
        'cibil': 'CIBIL',
        'crif': 'CRIF HIGH MARK',
        'experian': 'Experian'
      };
      return providerMap[report.details.apiProvider.toLowerCase()] || report.details.apiProvider;
    }
    return '';
  };

  // Ensure each modal open starts a fresh check flow instead of reusing old report state.
  useEffect(() => {
    if (open) {
      setForm({});
      setError('');
      setLoading(false);
      setReport(null);
      setShowDetails(false);
      setPdfLoading(false);
      setPdfReady(false);
      setPdfStatusChecking(false);
      setPdfUrl(null);
      setIsPdfViewerOpen(false);
      setIncludeCourtCase(false);
      setEquifaxTransactionId(null);
      setEquifaxReportData(null);
      setShowEquifaxStatus(false);
      setBureauReportData(null);
      setBureauReportNumber(null);
      setShowBureauStatus(false);
      setCourtCaseLoading(false);
      setCourtCaseStatus('');
      setCourtCasePolling(false);
      setCourtCaseCheckId(null);
      setPrefillMobile('');
      setPrefillMobile2('');
      setPrefillFirstName('');
      setPrefillMiddleName('');
      setPrefillLastName('');
      setPrefillLoading(false);
      setPrefillError('');
      setPrefillResult(null);
      setPrefillCheckId(null);
      setPrefillNotFound(false);
      setPrefillExhausted(false);
      setCibilDataOption('');
      return;
    }

    if (courtCaseTimerRef.current) {
      clearTimeout(courtCaseTimerRef.current);
      courtCaseTimerRef.current = null;
    }
    setCourtCasePolling(false);
    setCourtCaseLoading(false);
    setCourtCaseStatus('');
    setPdfLoading(false);
    setPdfReady(false);
    setPdfStatusChecking(false);
    setIsPdfViewerOpen(false);
    setForm({});

    if (pdfStatusTimerRef.current) {
      clearInterval(pdfStatusTimerRef.current);
      pdfStatusTimerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    const checkId = report?.overview?.checkId || report?.checkId || report?.id;

    if (!open || !checkId) {
      setPdfReady(false);
      setPdfStatusChecking(false);
      if (pdfStatusTimerRef.current) {
        clearInterval(pdfStatusTimerRef.current);
        pdfStatusTimerRef.current = null;
      }
      return;
    }

    let isMounted = true;
    let attempts = 0;
    const maxAttempts = 30;

    const refreshPdfStatus = async () => {
      try {
        if (!isMounted) return;
        setPdfStatusChecking(true);
        const statusResponse = await OrgUserService.getCreditReportPdfStatus(checkId);
        const ready = Boolean(statusResponse?.data?.pdfReady || statusResponse?.pdfReady);
        if (!isMounted) return;

        setPdfReady(ready);
        if (ready && pdfStatusTimerRef.current) {
          clearInterval(pdfStatusTimerRef.current);
          pdfStatusTimerRef.current = null;
        }
      } catch (statusErr) {
        console.warn('Failed to check PDF readiness status:', statusErr);
      } finally {
        if (isMounted) {
          setPdfStatusChecking(false);
        }
      }
    };

    refreshPdfStatus();
    pdfStatusTimerRef.current = setInterval(() => {
      attempts += 1;
      if (attempts >= maxAttempts) {
        clearInterval(pdfStatusTimerRef.current);
        pdfStatusTimerRef.current = null;
        return;
      }
      refreshPdfStatus();
    }, 3000);

    return () => {
      isMounted = false;
      if (pdfStatusTimerRef.current) {
        clearInterval(pdfStatusTimerRef.current);
        pdfStatusTimerRef.current = null;
      }
    };
  }, [open, report]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildNamePayload = () => {
    const nameParts = splitFullName(form.fullName);
    return {
      fullName: String(form.fullName || '').trim(),
      customerFirstName: nameParts.customerFirstName,
      customerMiddleName: nameParts.customerMiddleName,
      customerLastName: nameParts.customerLastName,
      hasMinimumParts: nameParts.hasMinimumParts,
    };
  };

  // Convert DD-MM-YYYY (API format) → YYYY-MM-DD (HTML date input format)
  const convertDobToInputFormat = (dob) => {
    if (!dob) return '';
    const parts = String(dob).split('-');
    if (parts.length === 3 && parts[0].length === 2) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dob;
  };

  const normalizeGender = (g) => {
    const val = String(g || '').toUpperCase();
    if (val.startsWith('M')) return 'Male';
    if (val.startsWith('F')) return 'Female';
    return '';
  };

  const buildAddressString = (addressArr) => {
    if (!Array.isArray(addressArr) || addressArr.length === 0) return '';
    // V2 returns addresses as plain strings
    if (typeof addressArr[0] === 'string') return addressArr[0].trim();
    const a = addressArr[0];
    return [
      a.first_line_of_address,
      a.second_line_of_address,
      a.third_line_of_address,
      a.city,
      a.state,
      a.postal_code,
    ].filter(Boolean).join(', ');
  };

  // A response counts as "found" only if it actually carries some identifying data
  const isPrefillDataEmpty = (d) => {
    if (!d) return true;
    const hasAddress = Array.isArray(d.address) && d.address.length > 0;
    return !d.name && !d.pan && !d.dob && !d.email && !hasAddress;
  };

  // attemptMobile: which mobile number this attempt is using (1st or 2nd field)
  // isSecondAttempt: true when this call is the retry using prefillMobile2
  const handlePrefillSearch = async (isSecondAttempt = false) => {
    setPrefillError('');
    setPrefillResult(null);

    const mobileToUse = isSecondAttempt ? prefillMobile2 : prefillMobile;

    if (!/^\d{10}$/.test(mobileToUse)) {
      setPrefillError('Enter a valid 10-digit mobile number');
      return;
    }
    if (!prefillFirstName.trim()) {
      setPrefillError('First name is required');
      return;
    }
    if (!prefillLastName.trim()) {
      setPrefillError('Last name is required');
      return;
    }

    setPrefillLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/credit-check/mobile-prefill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...OrgUserAuthService.getAuthHeader(),
        },
        body: JSON.stringify({
          mobileNumber: mobileToUse,
          firstName:    prefillFirstName.trim(),
          middleName:   prefillMiddleName.trim(),
          lastName:     prefillLastName.trim(),
        }),
      });

      const data = await res.json();
      const d = res.ok ? (data.data || {}) : null;

      if (!res.ok || isPrefillDataEmpty(d)) {
        if (!res.ok) {
          setPrefillError(data.error || 'Prefill lookup failed');
        }
        if (isSecondAttempt) {
          setPrefillExhausted(true);
          setPrefillNotFound(false);
        } else {
          setPrefillNotFound(true);
        }
        setPrefillLoading(false);
        return;
      }

      // Data found — clear retry state and populate the result + form
      setPrefillNotFound(false);
      setPrefillExhausted(false);
      setPrefillResult(d);
      setPrefillCheckId(data.checkId || null);

      // Pre-fill the form fields with returned data
      setForm((prev) => ({
        ...prev,
        fullName: d.name || prev.fullName || '',
        dateOfBirth: convertDobToInputFormat(d.dob) || prev.dateOfBirth || '',
        gender: normalizeGender(d.gender) || prev.gender || '',
        panNumber: d.pan || prev.panNumber || '',
        contactNumber: mobileToUse,
        permanentAddress: buildAddressString(d.address) || prev.permanentAddress || '',
        address: buildAddressString(d.address) || prev.address || '',
      }));
    } catch (err) {
      setPrefillError('Network error. Please try again.');
      if (isSecondAttempt) {
        setPrefillExhausted(true);
        setPrefillNotFound(false);
      } else {
        setPrefillNotFound(true);
      }
    }
    setPrefillLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const namePayload = buildNamePayload();
    
    if (!namePayload.fullName) {
      setError('Full Name is required');
      return;
    }
    if (!/^[A-Za-z\s]+$/.test(namePayload.fullName)) {
      setError('Full Name must contain only letters and spaces');
      return;
    }
    if (!namePayload.hasMinimumParts) {
      setError('Full Name must include at least first name and last name');
      return;
    }
    
    if (!form.dateOfBirth) {
      setError('Date of Birth is required');
      return;
    }
    
    if (!form.gender) {
      setError('Gender is required');
      return;
    }
    
    if (!form.contactNumber?.trim()) {
      setError('Mobile Number is required');
      return;
    }
    
    if (!/^\d{10}$/.test(form.contactNumber)) {
      setError('Mobile Number must be exactly 10 digits');
      return;
    }
    
    if (!form.panNumber?.trim()) {
      setError('PAN Number is required');
      return;
    }
    
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber)) {
      setError('PAN Number format is invalid (e.g., ABCDE1234F)');
      return;
    }
    
    if (!form.aadhaarNumber?.trim()) {
      setError('Aadhaar Number is required');
      return;
    }
    
    if (!/^\d{12}$/.test(form.aadhaarNumber)) {
      setError('Aadhaar Number must be exactly 12 digits');
      return;
    }
    
    if (!form.loanType) {
      setError('Loan Type is required');
      return;
    }
    
    if (!form.loanAmount) {
      setError('Loan Amount is required');
      return;
    }
    
    if (parseFloat(form.loanAmount) <= 0) {
      setError('Loan Amount must be greater than 0');
      return;
    }
    
    // Validate loan purpose if provided (optional field, but must contain only letters and spaces)
    if (form.loanPurpose && form.loanPurpose.trim() && !/^[A-Za-z\s]+$/.test(form.loanPurpose.trim())) {
      setError('Loan Purpose must contain only letters and spaces');
      return;
    }

    // Advanced/Combo Plans - Route based on mode and selected provider
    if (planType === 'Advanced' || planType === 'Combo') {
      if (mode === 'with-otp') {
        // WITH OTP: Use Equifax
        await handleInitiateEquifax();
      } else if (mode === 'without-otp') {
        // WITHOUT OTP: Use selected bureau provider (CIBIL/CRIF/Experian)
        if (!selectedProvider) {
          setError('Please select a bureau provider');
          return;
        }
        if (selectedProvider === 'cibil' && !cibilDataOption) {
          setError('Please select either "With Data" or "Without Data" for the CIBIL report');
          return;
        }
        await handleFetchBureauReportAndCreateCheck(selectedProvider);
      } else {
        setError('Invalid mode. Please select a valid credit check method.');
      }
      return;
    }

    // Basic Plan - existing flow
    setLoading(true);
    setReport(null);
    setShowDetails(false);
    
    try {
      const res = await fetch(`${API_BASE_URL}/credit-check`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...OrgUserAuthService.getAuthHeader()
        },
        body: JSON.stringify({
          ...form,
          ...namePayload,
          planType,
          apiProvider: selectedProvider || 'none',
          prefillData: prefillResult || null,
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.error || data.hint || 'Credit check failed');
        setLoading(false);
        return;
      }
      
      setReport(data);
    } catch (err) {
      console.error('Credit check error:', err);
      setError('Network error. Please check if backend is running.');
    }
    setLoading(false);
  };

  const extractErrorMessage = (data, defaultMsg) => {
    // Handle validation errors (422 responses with detail array)
    if (Array.isArray(data?.detail)) {
      const messages = data.detail.map(err => err.msg || JSON.stringify(err)).join('; ');
      return messages;
    }
    if (Array.isArray(data?.details?.detail)) {
      const messages = data.details.detail.map(err => err.msg || JSON.stringify(err)).join('; ');
      return messages;
    }
    
    // Handle standard error responses
    return data?.error || data?.details?.message || data?.hint || defaultMsg;
  };

  const handleInitiateEquifax = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/credit-check/equifax/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...OrgUserAuthService.getAuthHeader()
        },
        body: JSON.stringify({
          ...buildNamePayload(),
          contactNumber: form.contactNumber,
          panNumber: form.panNumber
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const detailedError = extractErrorMessage(
          data, 
          `Failed to initiate Credit Score session (HTTP ${response.status})`
        );
        setError(detailedError);
        setLoading(false);
        return;
      }

      setEquifaxTransactionId(data.transactionId);
      setShowEquifaxStatus(true);

      // Track if report received via postMessage
      let reportReceivedViaMessage = false;

      // Listen for postMessage from popup window
      const handleMessage = (event) => {
        // Security: verify origin in production
        if (event.data && event.data.type === 'EQUIFAX_REPORT_SUCCESS') {
          console.log('✅ Received Equifax report from popup');
          reportReceivedViaMessage = true;
          window.removeEventListener('message', handleMessage);
          
          const reportData = event.data.report;
          setEquifaxReportData(reportData);
          
          // Create credit check with received report data
          handleCreateCreditCheck({
            apiProvider: 'equifax',
            equifaxReportData: reportData
          });
        }
      };
      window.addEventListener('message', handleMessage);

      // Open Equifax redirect URL in new window
      if (data.redirectUrl) {
        const equifaxWindow = window.open(data.redirectUrl, 'equifax-consent', 'width=800,height=600');
        
        // Fallback: Poll for popup close (in case postMessage fails)
        const checkCompletion = setInterval(() => {
          if (equifaxWindow?.closed) {
            clearInterval(checkCompletion);
            window.removeEventListener('message', handleMessage);
            
            // Only fetch via API if we didn't receive postMessage
            if (!reportReceivedViaMessage) {
              console.log('⚠️ Popup closed without postMessage, trying API fallback...');
              setTimeout(() => {
                handleFetchEquifaxReportAndCreateCheck(data.transactionId);
              }, 1000);
            }
          }
        }, 500);
        
        // Cleanup listener after 5 minutes
        setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkCompletion);
        }, 5 * 60 * 1000);
      }
    } catch (err) {
      console.error('Error initiating Equifax:', err);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const handleFetchEquifaxReportAndCreateCheck = async (transactionId) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/credit-check/equifax/callback?transaction_id=${encodeURIComponent(transactionId)}`,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...OrgUserAuthService.getAuthHeader()
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const detailedError = extractErrorMessage(
          data,
          `Failed to fetch Credit Score report (HTTP ${response.status})`
        );
        setError(detailedError);
        setLoading(false);
        return;
      }

      const reportData = data.report || null;
      setEquifaxReportData(reportData);
      await handleCreateCreditCheck({
        apiProvider: 'equifax',
        equifaxReportData: reportData
      });
    } catch (err) {
      console.error('Error fetching Equifax report:', err);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const handleCreateCreditCheck = async ({
    apiProvider,
    equifaxReportData: equifaxDataOverride,
    bureauReportData: bureauDataOverride
  } = {}) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/credit-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...OrgUserAuthService.getAuthHeader()
        },
        body: JSON.stringify({
          ...form,
          ...buildNamePayload(),
          planType,
          apiProvider: apiProvider || selectedProvider || 'none',
          // Court case checkbox for Combo plan
          includeCourtCase: planType === 'Combo' ? includeCourtCase : undefined,
          // Equifax data (WITH OTP)
          equifaxReportData: equifaxDataOverride || (mode === 'with-otp' ? equifaxReportData : null),
          // Bureau data (WITHOUT OTP) - CIBIL/CRIF/Experian
          bureauReportData: bureauDataOverride || (mode === 'without-otp' ? bureauReportData : null),
          // Mobile-to-Prefill data
          prefillData: prefillResult || null,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const detailedError = [data?.error, data?.details].filter(Boolean).join(': ');
        setError(detailedError || 'Failed to create credit check');
        setLoading(false);
        return;
      }

      setReport(data);
      setShowEquifaxStatus(false);
      setShowBureauStatus(false);

      const checkId = data?.overview?.checkId || data?.checkId || data?.id;
      const courtCaseStatusValue = data?.details?.courtCase?.status;
      if (planType === 'Combo' && checkId && courtCaseStatusValue === 'pending') {
        setCourtCaseCheckId(checkId);
        setCourtCasePolling(true);
      }
    } catch (err) {
      console.error('Error creating advanced credit check:', err);
      setError('Network error. Please try again.');
    }

    setLoading(false);
  };

  // Fetch Bureau Report (CIBIL/CRIF/Experian) and create check (WITHOUT OTP)
  const handleFetchBureauReportAndCreateCheck = async (provider) => {
    setLoading(true);
    setError('');
    setShowBureauStatus(true);
    
    try {
      console.log(`📊 Fetching ${provider.toUpperCase()} report...`);
      
      // TODO: This endpoint needs to be created in backend
      const fetchResponse = await fetch(`${API_BASE_URL}/credit-check/bureau/${provider}/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...OrgUserAuthService.getAuthHeader()
        },
        body: JSON.stringify({
          ...buildNamePayload(),
          panNumber: form.panNumber,
          contactNumber: form.contactNumber,
          dateOfBirth: form.dateOfBirth,
          gender: form.gender,
          // CIBIL-only: user's per-request "With Data" (v3) / "Without Data" (v5) choice
          ...(provider === 'cibil' ? { cibilApiVersion: cibilDataOption === 'with-data' ? 'v3' : 'v5' } : {})
        })
      });

      const fetchData = await fetchResponse.json();

      if (!fetchResponse.ok) {
        const detailedError = extractErrorMessage(
          fetchData,
          `Failed to fetch ${provider.toUpperCase()} report (HTTP ${fetchResponse.status})`
        );
        setError(detailedError);
        setLoading(false);
        setShowBureauStatus(false);
        return;
      }

      console.log(`✅ ${provider.toUpperCase()} report fetched successfully`);
      
      // Store the complete bureau response (includes parsedData, rawData, score, etc.)
      setBureauReportData(fetchData);
      setBureauReportNumber(fetchData.reportNumber || fetchData.transactionId);

      // Step 2: Create credit check with complete bureau data
      await handleCreateCreditCheck({
        apiProvider: provider,
        bureauReportData: fetchData  // Send complete response with parsedData
      });

    } catch (err) {
      console.error(`Error fetching ${provider} report:`, err);
      setError('Network error. Please try again.');
      setLoading(false);
      setShowBureauStatus(false);
    }
  };

  const stopCourtCasePolling = () => {
    if (courtCaseTimerRef.current) {
      clearTimeout(courtCaseTimerRef.current);
      courtCaseTimerRef.current = null;
    }
    setCourtCasePolling(false);
  };

  const handleFetchCourtCaseResults = async (checkId, attempt, maxAttempts) => {
    setCourtCaseLoading(true);

    if (attempt === 1) {
      setCourtCaseStatus('Court case processing started...');
    } else {
      setCourtCaseStatus(`Court case check is in progress. Retrying (${attempt}/${maxAttempts})...`);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/credit-check/court-case/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...OrgUserAuthService.getAuthHeader()
        },
        body: JSON.stringify({ checkId })
      });

      let data = null;
      let rawText = '';
      try {
        rawText = await response.text();
        data = rawText ? JSON.parse(rawText) : null;
      } catch (parseError) {
        data = null;
      }

      if (response.status === 202 || data?.pending) {
        return { status: 'pending' };
      }

      if (response.ok) {
        setCourtCaseStatus('Court case results fetched and report updated.');
        setCourtCaseLoading(false);
        return { status: 'completed' };
      }

      const errorMessage = data?.error || data?.message || `Request failed with status ${response.status}`;
      setCourtCaseStatus(errorMessage);
      setCourtCaseLoading(false);
      return { status: 'failed' };
    } catch (err) {
      console.error('Court case fetch error:', err);
      setCourtCaseStatus(`Network error while fetching court case results. ${err?.message || ''}`.trim());
      setCourtCaseLoading(false);
      return { status: 'failed' };
    }
  };

  useEffect(() => {
    if (!courtCasePolling || !courtCaseCheckId) {
      return undefined;
    }

    const maxAttempts = 20;
    const delayMs = 3000;
    courtCaseAttemptsRef.current = 0;

    const run = async () => {
      if (!courtCasePolling) {
        return;
      }

      if (courtCaseAttemptsRef.current >= maxAttempts) {
        setCourtCaseStatus('Court case check is still in progress. Please try again later.');
        setCourtCaseLoading(false);
        stopCourtCasePolling();
        return;
      }

      courtCaseAttemptsRef.current += 1;
      const attempt = courtCaseAttemptsRef.current;
      const result = await handleFetchCourtCaseResults(courtCaseCheckId, attempt, maxAttempts);

      if (result.status === 'pending') {
        courtCaseTimerRef.current = setTimeout(run, delayMs);
        return;
      }

      stopCourtCasePolling();
    };

    run();

    return () => {
      if (courtCaseTimerRef.current) {
        clearTimeout(courtCaseTimerRef.current);
        courtCaseTimerRef.current = null;
      }
    };
  }, [courtCasePolling, courtCaseCheckId]);

  // Fetch court case cost when modal opens
  useEffect(() => {
    if (open && planType === 'Combo') {
      const fetchCourtCaseCost = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/orguser/credit-costs`, {
            headers: OrgUserAuthService.getAuthHeader()
          });
          const data = await response.json();
          if (data?.data?.courtCaseCheckCost) {
            setCourtCaseCost(parseFloat(data.data.courtCaseCheckCost));
          }
        } catch (err) {
          console.error('Failed to fetch court case cost:', err);
          // Use default cost
        }
      };
      fetchCourtCaseCost();
    }
  }, [open, planType]);

  const getScoreClass = (score) => {
    if (score < 550) return 'poor';
    if (score < 650) return 'fair';
    return 'good';
  };

  const handleViewFullReport = async () => {
    const checkId = report?.overview?.checkId || report?.checkId || report?.id;
    if (!checkId) {
      console.error('Credit check ID not available for PDF download', report);
      alert('Unable to open PDF: credit check id missing.');
      return;
    }

    if (!pdfReady) {
      alert('PDF is still generating. Please wait until status becomes ready.');
      return;
    }

    setPdfLoading(true);
    try {
      const headers = OrgUserAuthService.getAuthHeader();
      const response = await fetch(`${API_BASE_URL}/orguser/credit-checks/${checkId}/download`, { headers });

      let payload = null;
      try {
        payload = await response.json();
      } catch (error_) {
        console.warn('Failed to parse PDF download response as JSON:', error_);
      }

      if (!response.ok) {
        const msg = payload?.error || payload?.message || 'Failed to fetch report.';
        throw new Error(msg);
      }

      const url = payload?.data?.url || payload?.url || payload?.downloadUrl || payload?.pdfUrl;
      if (!url) {
        alert('PDF is still generating. Please try again in a few seconds.');
        return;
      }

      window.open(url, '_blank');
    } catch (err) {
      console.error('Error fetching PDF:', err);
      alert(err?.message || 'Failed to load PDF report. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  return open ? (
    <div className="credit-modal-overlay" >
        <div className={`credit-modal-content ${showDetails ? 'narrow' : 'wide'}`} onClick={e => e.stopPropagation()}>
          <div className="credit-modal-header">
            <h2>Credit Score Check - {planType} Plan {getProviderDisplayName() ? `- ${getProviderDisplayName()}` : ''}</h2>
            <button className="credit-modal-close" onClick={loading || prefillLoading ? undefined : onClose} disabled={loading || prefillLoading}>×</button>
          </div>

          <div className="credit-modal-body">

            {/* Customer Lookup — shown above the credit check form for Advanced/Combo plans */}
            {planType !== 'Basic' && !loading && !report && (
              <div className="prefill-step-container">
                <div className="prefill-step-header">
                  <div className="prefill-step-icon">📱</div>
                  <div>
                    <h3 className="prefill-step-title">Customer Lookup via Mobile</h3>
                    <p className="prefill-step-desc">Enter the borrower's mobile number and name to fetch their details from the credit bureau. A Prefill Report will be saved in History.</p>
                  </div>
                </div>

                <div className="prefill-fields-grid">
                  <div className="form-field">
                    <label className="form-label">First Name<span className="required-mark"> *</span></label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter first name"
                      value={prefillFirstName}
                      onChange={(e) => setPrefillFirstName(e.target.value)}
                      disabled={prefillLoading}
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Middle Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter middle name"
                      value={prefillMiddleName}
                      onChange={(e) => setPrefillMiddleName(e.target.value)}
                      disabled={prefillLoading}
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Last Name<span className="required-mark"> *</span></label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter last name"
                      value={prefillLastName}
                      onChange={(e) => setPrefillLastName(e.target.value)}
                      disabled={prefillLoading}
                    />
                  </div>
                </div>

                <div className="prefill-mobile-row">
                  <div className="form-field prefill-field-mobile">
                    <label className="form-label">Mobile Number<span className="required-mark"> *</span></label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="10 digit mobile number"
                      value={prefillMobile}
                      onChange={(e) => setPrefillMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      disabled={prefillLoading || prefillNotFound || prefillExhausted}
                      maxLength={10}
                    />
                  </div>
                  {prefillNotFound && !prefillExhausted && (
                    <div className="form-field prefill-field-mobile">
                      <label className="form-label">Alternate Mobile Number<span className="required-mark"> *</span></label>
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="Enter another 10 digit mobile number"
                        value={prefillMobile2}
                        onChange={(e) => setPrefillMobile2(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        disabled={prefillLoading}
                        maxLength={10}
                        autoFocus
                      />
                    </div>
                  )}
                </div>

                {prefillError && <div className="prefill-error">{prefillError}</div>}

                {prefillNotFound && !prefillExhausted && (
                  <div className="prefill-info-banner">
                    No records found for this mobile number. Please enter an alternate mobile number above to continue the search.
                  </div>
                )}

                {prefillExhausted && (
                  <div className="prefill-info-banner">
                    We were unable to find any records using either mobile number. Please proceed by filling in the Credit Check form below manually.
                  </div>
                )}

                {prefillResult && (
                  <div className="prefill-result-card">
                    <div className="prefill-result-title">✅ Customer Found</div>
                    <div className="prefill-result-grid">
                      {prefillResult.name  && <div className="prefill-result-item"><span className="prefill-result-key">Name</span><span className="prefill-result-val">{prefillResult.name}</span></div>}
                      {prefillResult.dob   && <div className="prefill-result-item"><span className="prefill-result-key">DOB</span><span className="prefill-result-val">{prefillResult.dob}</span></div>}
                      {prefillResult.gender && <div className="prefill-result-item"><span className="prefill-result-key">Gender</span><span className="prefill-result-val">{prefillResult.gender}</span></div>}
                      {prefillResult.pan   && <div className="prefill-result-item"><span className="prefill-result-key">PAN</span><span className="prefill-result-val">{prefillResult.pan}</span></div>}
                      {prefillResult.email && <div className="prefill-result-item"><span className="prefill-result-key">Email</span><span className="prefill-result-val">{prefillResult.email}</span></div>}
                      {prefillResult.score && <div className="prefill-result-item"><span className="prefill-result-key">Score</span><span className="prefill-result-val prefill-score-highlight">{prefillResult.score}</span></div>}
                    </div>
                    <div className="prefill-pdf-note">📄 Prefill Report saved to History (Check ID: {prefillCheckId})</div>
                  </div>
                )}

                {!prefillResult && !prefillExhausted && (
                  <div className="prefill-step-actions">
                    <button
                      type="button"
                      className="btn-prefill-search"
                      onClick={() => handlePrefillSearch(prefillNotFound)}
                      disabled={prefillLoading || (prefillNotFound && !/^\d{10}$/.test(prefillMobile2))}
                    >
                      {prefillLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* CIBIL report type — mandatory per-request choice (replaces the old
                SuperAdmin-level v3/v5 toggle): "With Data" -> v3, "Without Data" -> v5 */}
            {mode === 'without-otp' && selectedProvider === 'cibil' && (planType === 'Advanced' || planType === 'Combo') && !loading && !report && (
              <div className="cibil-data-option-section">
                <label className="form-label">
                  CIBIL Report Type<span className="required-mark"> *</span>
                </label>
                <div className="cibil-data-option-row">
                  <label className="cibil-data-option-checkbox">
                    <input
                      type="checkbox"
                      checked={cibilDataOption === 'with-data'}
                      onChange={() => setCibilDataOption('with-data')}
                    />
                    <span>With Data</span>
                  </label>
                  <label className="cibil-data-option-checkbox">
                    <input
                      type="checkbox"
                      checked={cibilDataOption === 'without-data'}
                      onChange={() => setCibilDataOption('without-data')}
                    />
                    <span>Without Data</span>
                  </label>
                </div>
                {!cibilDataOption && (
                  <div className="cibil-data-option-hint">Please select one option to continue.</div>
                )}
              </div>
            )}

            {!loading && !report && (
              <div className="plan-info-box">
                <div>
                  <strong>{planType} Plan:</strong> {planDescriptions[planType]}
                </div>
                <div className="plan-info-note">All charges will be auto-deducted from your wallet.</div>
              </div>
            )}
            
            {showEquifaxStatus && (
              <div className="equifax-status-container">
                <div className="equifax-status-icon">🔗</div>
                <h3>Credit Score Verification in Progress</h3>
                <p>We've redirected you to Credit Score for credit authorization.</p>
                <p className="equifax-status-note">
                  ✓ Please complete the verification process in the new window<br/>
                  ✓ Once completed, we'll automatically fetch your credit report
                </p>
              </div>
            )}

            {courtCaseStatus && (
              <div className="info-message">
                {courtCaseLoading ? '⟳ ' : '✓ '} {courtCaseStatus}
              </div>
            )}
            
            {loading ? (
              <div className="loading-container">
                <div className="logo-spinner-wrapper">
                  <img src="/logo.svg" alt="LCS Logo" className="spinner-logo" />
                  <div className="spinner-ring" />
                </div>
                <div className="loading-text-primary">Analyzing Credit Score...</div>
                <div className="loading-text-secondary">Fetching customer data and calculating credit worthiness</div>
                <div className="loading-steps">
                  <div className="loading-step">✓ Validating customer information</div>
                  <div className="loading-step">⟳ Processing credit history</div>
                  <div className="loading-step">○ Generating report</div>
              </div>
            </div>
          ) : report ? (
            <div>
              <div className="report-overview">
                <h3>{report.overview.customer}</h3>
                <div className={`report-score ${getScoreClass(report.overview.score)}`}>
                  {report.overview.score}
                </div>
                <div className="report-status">{report.overview.status}</div>
                <div className="report-check-id">Check ID: {report.overview.reportId}</div>
                <div className="report-meta">Date: {report.overview.date} | Cost: ₹{report.overview.creditCost}</div>
              </div>

              {report.overview.scoreFactors && report.overview.scoreFactors.length > 0 && (
                <div className="key-factors-section">
                  <h4>Key Factors Affecting Score</h4>
                  {report.overview.scoreFactors.map((factor, idx) => (
                    <div key={idx} className="factor-item">
                      <div className="factor-name">{factor.factor}</div>
                      <div className="factor-impact">
                        Impact: <strong className={
                          factor.impact.startsWith('+') ? 'impact-positive' : 
                          factor.impact.startsWith('-') ? 'impact-negative' : 
                          'impact-neutral'
                        }>{factor.impact}</strong> 
                        {factor.weight && ` (Weight: ${factor.weight})`}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="report-actions">
                {!pdfReady && (
                  <div className="info-message pdf-status-wait" style={{ marginBottom: '10px' }} role="status" aria-live="polite">
                    <span className="pdf-wait-loader" aria-hidden="true" />
                    <span>Please wait, your report PDF is being generated. The button will be enabled once it is ready.</span>
                  </div>
                )}
                {pdfReady && !pdfLoading && (
                  <div className="info-message" style={{ marginBottom: '10px' }}>
                    ✅ Report is ready. You can now open the full PDF.
                  </div>
                )}
                <button 
                  className="btn-report-primary" 
                  onClick={handleViewFullReport}
                  disabled={pdfLoading || !pdfReady || pdfStatusChecking}
                  title={pdfReady ? 'Open PDF report in new tab' : 'PDF generation is in progress'}
                >
                  {pdfLoading ? 'Loading Report...' : (pdfStatusChecking ? 'Checking PDF Status...' : (pdfReady ? '📄 View Full Report' : 'PDF Generating...'))}
                </button>
                <button 
                  className="btn-report-secondary" 
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? 'Hide Details' : 'View Details'}
                </button>
              </div>

              {showDetails && report.details && (
                <div className="detailed-report">
                  <h4>Detailed Report</h4>
                  
                  {report.details.customerInfo && (
                    <div className="detail-section">
                      <h5>Customer Information</h5>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Name</div>
                          <div className="info-value">{report.details.customerInfo.name}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Contact</div>
                          <div className="info-value">{report.details.customerInfo.contact}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Date of Birth</div>
                          <div className="info-value">{report.details.customerInfo.dateOfBirth}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Gender</div>
                          <div className="info-value">{report.details.customerInfo.gender}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {report.details.loanInfo && (
                    <div className="detail-section">
                      <h5>Loan Request Information</h5>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Loan Amount</div>
                          <div className="info-value">₹{report.details.loanInfo.amount}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Loan Type</div>
                          <div className="info-value">{report.details.loanInfo.type}</div>
                        </div>
                        <div className="info-item ccm-info-item-span-2">
                          <div className="info-label">Loan Purpose</div>
                          <div className="info-value">{report.details.loanInfo.purpose}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {report.details.allFactors && report.details.allFactors.length > 0 && (
                    <div className="detail-section">
                      <h5>All Score Factors</h5>
                      {report.details.allFactors.map((factor, idx) => (
                        <div key={idx} className="factor-item">
                          <div className="factor-name">{factor.factor}</div>
                          <div className="factor-impact">
                            Impact: <strong className={
                              factor.impact.startsWith('+') ? 'impact-positive' : 
                              factor.impact.startsWith('-') ? 'impact-negative' : 
                              'impact-neutral'
                            }>{factor.impact}</strong>
                            {factor.weight && ` (Weight: ${factor.weight})`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {report.details.customerData && (
                    <div className="detail-section">
                      <h5>Uploaded Customer Data</h5>
                      <div className="info-grid customer-data-grid">
                        <div className="info-item">
                          <div className="info-label">Account Number</div>
                          <div className="info-value">{report.details.customerData.accountNumber}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Account Type</div>
                          <div className="info-value">{report.details.customerData.accountType}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Date Opened</div>
                          <div className="info-value">{report.details.customerData.dateOpened}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Current Balance</div>
                          <div className="info-value">₹{report.details.customerData.currentBalance}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Credit Limit</div>
                          <div className="info-value">₹{report.details.customerData.creditLimit}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Days Past Due</div>
                          <div className={`info-value ${report.details.customerData.daysPastDue > 0 ? 'days-past-due-negative' : 'days-past-due-positive'}`}>
                            {report.details.customerData.daysPastDue}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {report.details.message && (
                    <div className="info-message">
                      ℹ️ {report.details.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <form className="credit-form" onSubmit={handleSubmit}>
              <div className="form-layout">
                <div className="form-section">
                  <h4 className="form-section-title">About Borrower</h4>
                  <div className="form-grid">
                    <div className="form-field full-width">
                      <label className="form-label">
                        Full Name<span className="required-mark"> *</span>
                      </label>
                      <input
                        name="fullName"
                        type="text"
                        className="form-input"
                        value={form.fullName || ''}
                        onChange={handleChange}
                        required
                        placeholder="Enter full name"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label">
                        Date of Birth<span className="required-mark"> *</span>
                      </label>
                      <input
                        name="dateOfBirth"
                        type="date"
                        className="form-input"
                        value={form.dateOfBirth || ''}
                        onChange={handleChange}
                        required
                        placeholder="dd-mm-yyyy"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Gender<span className="required-mark"> *</span></label>
                      <select
                        name="gender"
                        className="form-select"
                        value={form.gender || ''}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-field full-width">
                      <label className="form-label">
                        Mobile Number<span className="required-mark"> *</span>
                      </label>
                      <input
                        name="contactNumber"
                        type="tel"
                        className="form-input"
                        value={form.contactNumber || ''}
                        onChange={handleChange}
                        required
                        placeholder="10 digit mobile number"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label className="form-label">
                        Permanent Address
                      </label>
                      <textarea
                        name="permanentAddress"
                        rows="3"
                        className="form-textarea"
                        value={form.permanentAddress || ''}
                        onChange={handleChange}
                        placeholder="Flat / Street / City / State / PIN"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="address-identity-column">
                  <div className="form-section">
                    <h4 className="form-section-title">Current Address</h4>
                    <textarea
                      name="address"
                      rows="3"
                      className="form-textarea"
                      value={form.address || ''}
                      onChange={handleChange}
                      placeholder="Flat / Street / City / State / PIN"
                      disabled={loading}
                    />
                  </div>
                  <div className="form-section">
                    <h4 className="form-section-title">Proof Of Identity</h4>
                    <div className="identity-grid">
                      <div className="form-field">
                        <label className="form-label">PAN<span className="required-mark"> *</span></label>
                        <input
                          name="panNumber"
                          type="text"
                          className="form-input"
                          value={form.panNumber || ''}
                          onChange={handleChange}
                          required
                          placeholder="ABCDE1234F"
                          disabled={loading}
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Aadhaar<span className="required-mark"> *</span></label>
                        <input
                          name="aadhaarNumber"
                          type="text"
                          className="form-input"
                          value={form.aadhaarNumber || ''}
                          onChange={handleChange}
                          required
                          placeholder="12 digit Aadhaar"
                          disabled={loading}
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Ration Card</label>
                        <input
                          name="rationCardNumber"
                          type="text"
                          className="form-input"
                          value={form.rationCardNumber || ''}
                          onChange={handleChange}
                          placeholder="Ration Card Number"
                          disabled={loading}
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Voter ID</label>
                        <input
                          name="voterIdNumber"
                          type="text"
                          className="form-input"
                          value={form.voterIdNumber || ''}
                          onChange={handleChange}
                          placeholder="Voter ID Number"
                          disabled={loading}
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Driving License</label>
                        <input
                          name="drivingLicenseNumber"
                          type="text"
                          className="form-input"
                          value={form.drivingLicenseNumber || ''}
                          onChange={handleChange}
                          placeholder="Driving License Number"
                          disabled={loading}
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Passport</label>
                        <input
                          name="passportNumber"
                          type="text"
                          className="form-input"
                          value={form.passportNumber || ''}
                          onChange={handleChange}
                          placeholder="Passport Number"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="loan-details-section">
                <h4 className="form-section-title">Loan Details</h4>
                <div className="loan-details-grid">
                  <div className="form-field">
                    <label className="form-label">Loan Amount (₹)<span className="required-mark"> *</span></label>
                    <input
                      name="loanAmount"
                      type="number"
                      className="form-input"
                      value={form.loanAmount || ''}
                      onChange={handleChange}
                      placeholder="Enter loan amount"
                      required
                      min="1"
                      step="0.01"
                      disabled={loading}
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Loan Type<span className="required-mark"> *</span></label>
                    <select
                      name="loanType"
                      className="form-select"
                      value={form.loanType || ''}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Select Loan Type</option>
                      <option value="Personal Loan">Personal Loan</option>
                      <option value="Home Loan">Home Loan</option>
                      <option value="Auto Loan">Auto Loan</option>
                      <option value="Business Loan">Business Loan</option>
                      <option value="Education Loan">Education Loan</option>
                      <option value="Gold Loan">Gold Loan</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Purpose</label>
                    <input
                      name="loanPurpose"
                      type="text"
                      className="form-input"
                      value={form.loanPurpose || ''}
                      onChange={handleChange}
                      placeholder="E.g. Working capital"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Court Case Check Option for Combo Plan */}
              {planType === 'Combo' && (
                <div className="court-case-section">
                  <div className="court-case-checkbox-wrapper">
                    <label className="court-case-checkbox-label">
                      <input
                        type="checkbox"
                        className="court-case-checkbox"
                        checked={includeCourtCase}
                        onChange={(e) => setIncludeCourtCase(e.target.checked)}
                        disabled={loading}
                      />
                      <span className="checkbox-text">
                        <strong>Include Court Case Check</strong>
                        <span className="court-case-cost-badge">+ ₹{courtCaseCost}</span>
                      </span>
                    </label>
                    <p className="court-case-description">
                      {includeCourtCase 
                        ? `Court case verification will be included. Additional cost of ₹${courtCaseCost} will be charged.`
                        : 'Optional: Verify if the customer has any pending court cases.'
                      }
                    </p>
                  </div>
                  {includeCourtCase && (
                    <div style={{
                      background: '#fff8e1',
                      border: '1px solid #f9a825',
                      borderRadius: '6px',
                      padding: '10px 14px',
                      marginTop: '10px',
                      fontSize: '12px',
                      color: '#5f4000',
                      lineHeight: '1.5'
                    }}>
                      <strong>⚠️ Disclaimer:</strong> Court case data is fetched from public judicial records and matched based on name, address, and personal details provided. Due to common names or similar personal details, results may occasionally include cases belonging to different individuals. Please cross-verify all court case findings independently before making any credit or legal decisions.
                    </div>
                  )}
                </div>
              )}
              
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              
              <button 
                className="submit-button" 
                type="submit" 
                disabled={loading}
              >
                Enquire Now
              </button>
              
              <div className="form-note">
                Note: All fields marked with <span className="required-mark">*</span> are mandatory
              </div>
            </form>
          )}
          </div>
        </div>
        <PdfViewerModal
          isOpen={isPdfViewerOpen}
          toggle={() => setIsPdfViewerOpen(false)}
          fileUrl={pdfUrl}
          title="Credit Assessment Report"
        />
    </div>
  ) : null;
}
